/**
 * Renders the before/after diff returned by `POST /admin/groups/.../preview`.
 * Highlights operations and accounts added or removed, calls out production
 * access changes, and surfaces the affected user count.
 */

import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import { DiffResponse } from '../service/adminApiService';

export interface PermissionDiffPanelProps {
  diff: DiffResponse;
}

const List: React.FC<{ items: string[]; tone: 'added' | 'removed' }> = ({ items, tone }) => {
  if (items.length === 0) {
    return <Text type="b200" color="secondary">(none)</Text>;
  }
  const prefix = tone === 'added' ? '+ ' : '− ';
  const color = tone === 'added' ? '#006633' : '#B30017';
  return (
    <Column spacing="none">
      {items.map((item) => (
        <Text key={item} type="b200" color="primary" tag="span">
          <span style={{ color, fontFamily: 'monospace' }}>{prefix}</span>
          {item}
        </Text>
      ))}
    </Column>
  );
};

export const PermissionDiffPanel: React.FC<PermissionDiffPanelProps> = ({ diff }) => {
  const { diff: d, affected_user_count, affected_users } = diff;
  const isCreate = d.mode === 'create';

  return (
    <Column spacing="medium" spacingInset="400" tag="section" aria-label="Permission diff">
      {isCreate ? (
        <Alert type="neutral" title="New group">
          This group doesn't exist yet. Saving creates it with the values below.
        </Alert>
      ) : (
        <Alert type="neutral" title="Review changes before saving">
          {affected_user_count} user{affected_user_count === 1 ? '' : 's'} currently in this group
          will be affected.
        </Alert>
      )}

      <Row widths={['fill', 'fill']} spacing="large" alignmentVertical="top" wrap="down">
        <Column spacing="small">
          <Text type="h100">Operations</Text>
          <Text type="b300">Added</Text>
          <List items={d.operations_added} tone="added" />
          <Text type="b300">Removed</Text>
          <List items={d.operations_removed} tone="removed" />
        </Column>
        <Column spacing="small">
          <Text type="h100">Accounts</Text>
          <Text type="b300">
            Added ({d.accounts_added.length})
          </Text>
          <List items={d.accounts_added} tone="added" />
          <Text type="b300">
            Removed ({d.accounts_removed.length})
          </Text>
          <List items={d.accounts_removed} tone="removed" />
        </Column>
      </Row>

      {d.prod_access_changed && (
        <Alert
          type={d.after_prod_access ? 'warning' : 'neutral'}
          title={
            d.after_prod_access
              ? 'Production access will be ENABLED'
              : 'Production access will be DISABLED'
          }
        >
          Was: {d.before_prod_access ? 'enabled' : 'disabled'} → becomes:{' '}
          {d.after_prod_access ? 'enabled' : 'disabled'}
        </Alert>
      )}

      {affected_users.length > 0 && (
        <Column spacing="xsmall">
          <Text type="b300">Users in this group (first {affected_users.length})</Text>
          <Text type="b200" color="secondary">
            {affected_users.join(', ')}
            {affected_user_count > affected_users.length
              ? ` and ${affected_user_count - affected_users.length} more…`
              : ''}
          </Text>
        </Column>
      )}
    </Column>
  );
};
